import {Injectable} from "@angular/core";
import {DateRange} from "@angular/material/datepicker";
import * as moment from "moment/moment";
import {Moment, unitOfTime} from "moment/moment";
import StartOf = moment.unitOfTime.StartOf;

/**
 * Anything these helpers accept as a date. {@code number[]} is included deliberately:
 * it is the shape a Jackson-serialised {@code java.time} value arrives in, and API
 * responses reach these helpers through {@code any}-typed models, so the array case is
 * real at runtime whether or not a caller's type says so.
 */
export type DateLike = Date | string | number | number[] | Moment | null;

@Injectable({
  providedIn: 'root'
})
export class LibDateUtilsService {

  constructor() {
    moment.locale('en-za');
  }

  /**
   * The single parse used by every helper below.
   *
   * <p>Jackson serialises {@code java.time} values as arrays — {@code [2026,8,3]} for a
   * {@code LocalDate}, {@code [2026,8,3,14,30,0,123000000]} for a {@code LocalDateTime} —
   * with a <strong>1-based</strong> month, matching {@code java.time}. moment reads an
   * array's month as <strong>0-based</strong>, so handing one straight to {@code moment()}
   * silently shifts the date by a month, with no error anywhere.</p>
   *
   * <p>That is the root cause of TF-3, found on the CS-823 dev smoke: an offering starting
   * 3 August rendered as 3 September, and saving the form back persisted the shifted date —
   * silent, repeatable data corruption on a routine view-and-save. It was contained at the
   * three admin offering DTOs by pinning them to ISO strings; this fixes the parse itself,
   * so any other endpoint returning a bare {@code LocalDate} into one of these helpers is
   * read the way the backend wrote it.</p>
   *
   * <p>Non-array input is passed to moment untouched — nothing else changes.</p>
   */
  private toMoment(date: DateLike): Moment {
    if (Array.isArray(date)) {
      const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0, nano = 0] = date;
      // moment's 7th array element is milliseconds where Jackson writes nanoseconds.
      return moment([year, month - 1, day, hour, minute, second, Math.round(nano / 1_000_000)]);
    }
    return moment(date as Date | string | number | Moment);
  }

  displayOffsetDateTime(date: DateLike) {
    if (date) return this.toMoment(date).format("YYYY-MM-DD HH:mm:ss.SSS");
    return null;
  }

  displayShortDateUnix(date: number | any): string | null {
    if (date) return moment.unix(date).format("YYYY-MM-DD");
    return null;
  }

  displayShortDate(date: DateLike): string | null {
    if (date) {
      if (typeof date === 'number') {
        const reasonableMaxUnixSeconds = 4000000000;
        if (date < reasonableMaxUnixSeconds) {
          return moment.unix(date).format("YYYY-MM-DD");
        }
      }
      return this.toMoment(date).format("YYYY-MM-DD");
    }
    return null;
  }

  displayFullDate(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("Do MMMM YYYY");
    return null;
  }

  displayMonthAndYear(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("MMM YYYY");
    return null;
  }

  displayFullMonthAndYear(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("MMMM YYYY");
    return null;
  }

  displayDateAndMonth(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("DD MMM");
    return null;
  }

  displayDateWithTime(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("YYYY-MM-DD (hh:mm)");
    return null;
  }

  displayDayAndDateWithTime(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("ddd DD MMM hh:mm");
    return null;
  }

  displayYear(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("YYYY");
    return null;
  }

  displayFullMonth(date: DateLike): string | null {
    if (date) return this.toMoment(date).format("MMMM");
    return null;
  }

  displayDateDuration(date1: DateLike, date2: DateLike) {
    const start = this.toMoment(date1);
    const end = this.toMoment(date2);

    const months = end.diff(start, 'months');
    const days = end.diff(start, 'days');

    return months > 0 ? `${months} month${months > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''}`;
  }

  displayDateDurationDays(date: DateLike) {
    const days = this.toMoment(date).diff(moment(), 'days');
    return days > 0 ? this.toMoment(date).diff(moment(), 'days') + ' days' : '0 days';
  }

  getCurrentDate(): Date {
    return new Date();
  }

  /**
   * Parses a value into a local-midnight {@link Date} suitable for a MatDatepicker
   * control. moment parses a 'YYYY-MM-DD' string in local time (unlike the native
   * {@code new Date('YYYY-MM-DD')}, which parses as UTC and can offset the day).
   */
  toDate(date: DateLike): Date | null {
    if (date) return this.toMoment(date).toDate();
    return null;
  }

  isPastDated(date: DateLike): boolean {
    return this.toMoment(date).isBefore(new Date());
  }

  isFutureDated(date: DateLike): boolean {
    return this.toMoment(date).isAfter(new Date());
  }

  isPresentDated(date: DateLike): boolean {
    return this.toMoment(date).isSame(new Date(), "day");
  }

  getFirstDayOfMonth(date: DateLike): string {
    return this.toMoment(date).startOf('month').format('YYYY-MM-DD');
  }

  getFirstDayOfYear(date: DateLike): string {
    return this.toMoment(date).startOf('year').format('YYYY-MM-DD');
  }

  getLastDayOfMonth(date: DateLike): string {
    return this.toMoment(date).endOf('month').format('YYYY-MM-DD');
  }

  getNumberOfDaysInMonth(date: DateLike): number {
    return this.toMoment(date).daysInMonth();
  }

  getFirstDayOfQuarter(date: DateLike): string {
    return this.toMoment(date).startOf('quarter').format('YYYY-MM-DD');
  }

  getLastDayOfQuarter(date: DateLike): string {
    return this.toMoment(date).endOf('quarter').format('YYYY-MM-DD');
  }

  isWeekday(date: DateLike) {
    return !(this.toMoment(date).weekday() == 0 || this.toMoment(date).weekday() == 6);
  }

  doesOverlap(range1: DateRange<string | Date>, range2: DateRange<string | Date>) {
    return this.toMoment(range1.start).isSameOrBefore(this.toMoment(range2.end)) && this.toMoment(range1.end).isSameOrAfter(this.toMoment(range2.start));
  }

  datesInclude(dates: Date[], date: Date) {
    let includes = false;
    dates.forEach(ea => {
      includes = this.toMoment(ea).isSame(date, 'day') || includes;
    })
  }

  inRange(date: DateLike, range: DateRange<string | Date>) {
    return this.toMoment(date).isSameOrBefore(this.toMoment(range.end)) && this.toMoment(date).isSameOrAfter(this.toMoment(range.start));
  }

  getDaysBetweenIncl(date1: DateLike, date2: DateLike) {
    if (!date2) {
      date2 = new Date();
    }
    return this.toMoment(date2).diff(this.toMoment(date1), 'days');
  }

  isBefore(date1: DateLike, date2: DateLike) {
    return this.toMoment(date1).isBefore(this.toMoment(date2));
  }

  isStartOfCurrentPeriod(date: DateLike, period: StartOf) {
    return this.toMoment(date).isSame(moment(new Date()).startOf(period), 'day');
  }

  getWeeksOfMonth(date: DateLike) {
    const weeks = [];

    const firstDay = this.toMoment(date).startOf('month');
    const lastDay = this.toMoment(date).endOf('month');
    let currentSunday = this.toMoment(firstDay).startOf('week');

    while (currentSunday.isSameOrBefore(lastDay)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        if (currentSunday.isSameOrBefore(lastDay)) {
          if (currentSunday.month() == lastDay.month()) {
            week.push(currentSunday.clone());
          }
          currentSunday.add(1, 'day');
        }
      }
      weeks.push(week);
    }
    return weeks;
  }

  getNumberOfPeriods(date1: DateLike, date2: DateLike, period: unitOfTime.Diff) {
    const start = this.toMoment(date1);
    const end = this.toMoment(date2);
    return end.add(1, "day").diff(start, period, true);
  }

  getNumberOfWeekends(startDate: DateLike, endDate: DateLike, includeFridays: boolean = false) {
    const start = this.toMoment(startDate);
    const end = this.toMoment(endDate).add(1, "day");

    let count = 0;
    let current = start.clone();

    while (current < end) {
      const day = current.day(); // 0 = Sunday, 6 = Saturday, 5 = Friday
      if (includeFridays && (day === 0 || day === 6 || day == 5)) {
        count++;
      }

      if (!includeFridays && (day === 0 || day === 6)) {
        count++;
      }

      current.add(1, "day");
    }

    const weekendLength = includeFridays ? 3 : 2;

    return count/weekendLength;
  }

  add(date: DateLike, value: number, units: unitOfTime.DurationConstructor): Date | null {
    if (date) return this.toMoment(date).add(value, units).toDate();
    return null
  }
}
